import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { MessageCard } from "@/components/MessageCard";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, BookOpen } from "lucide-react";

const PAGE_SIZE = 12;

export default function Archivo() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tags").then((res) => setTags(res.data.tags || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchMessages = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("skip", String(page * PAGE_SIZE));
    params.set("limit", String(PAGE_SIZE));
    if (selectedTag) params.set("tag", selectedTag);
    if (debouncedSearch) params.set("search", debouncedSearch);
    api
      .get(`/messages?${params.toString()}`)
      .then((res) => {
        setMessages(res.data.messages || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, selectedTag, debouncedSearch]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    setPage(0);
  }, [selectedTag, debouncedSearch]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="mb-8 max-w-2xl">
        <div className="flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-primary" />
          <h1 className="font-serif text-3xl font-semibold sm:text-4xl">Archivo de mensajes</h1>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Todos los mensajes de fortaleza espiritual, ordenados por fecha. Busca por tema o palabra.
        </p>
      </div>

      <div className="mb-6 flex flex-col gap-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título, pasaje o tema…"
            className="bg-card pl-9"
            data-testid="archivo-search-input"
          />
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="archivo-tag-filter-bar">
            <Badge
              onClick={() => setSelectedTag(null)}
              className={`cursor-pointer ${!selectedTag ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent"}`}
              data-testid="archivo-tag-todos"
            >
              todos
            </Badge>
            {tags.map((tag) => (
              <Badge
                key={tag}
                onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                className={`cursor-pointer ${selectedTag === tag ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent"}`}
                data-testid={`archivo-tag-${tag}`}
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-56 w-full rounded-[var(--radius)]" />
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div className="rounded-[var(--radius)] border border-border bg-card p-10 text-center" data-testid="archivo-empty-state">
          <p className="font-serif text-xl font-semibold">Aún no hay mensajes para este filtro</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Prueba con otro tema o vuelve pronto. Cada día compartimos una nueva palabra de esperanza.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {messages.map((m) => (
              <MessageCard key={m.id} message={m} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="archivo-prev-page">
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">
                Página {page + 1} de {totalPages}
              </span>
              <Button variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="archivo-next-page">
                Siguiente
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
